import { useMemo, useState } from 'react';
import { geoMercator, geoPath, geoArea } from 'd3-geo';
import { feature } from 'topojson-client';
import topo from './data/uk-postcode-areas.json';
import './UKPostcodeMap.css';

// ── Region / area structure (mirrors the printed coverage map) ──────────────
export const AREA_COLORS = {
  1: '#FFD200', // Area 1 — yellow
  2: '#F6921E', // Area 2 — orange
  3: '#2456A6', // Area 3 — blue
  4: '#9C5FB5', // Area 4 — purple
  5: '#D22730', // Area 5 — red
};
const ORBITAL_GREY = '#6b6d70';
const OUTSIDE_GREY = 'rgba(255, 255, 255, 0.16)';

const R = (region, area, codes) =>
  Object.fromEntries(codes.map((c) => [c, { region, area }]));

export const POSTCODE_REGIONS = {
  ...R('Orbital', 1, ['W', 'WC', 'TW', 'SW', 'SM', 'KT', 'CR']),
  ...R('Orbital', 2, ['SE', 'BR', 'DA']),
  ...R('Orbital', 3, ['WD', 'EN', 'N', 'IG', 'E', 'RM', 'HA', 'NW', 'UB', 'EC']),
  ...R('North', 1, ['LL', 'SY', 'ST', 'TF', 'WS', 'WV', 'DY', 'B', 'WR', 'LD', 'HR', 'SA', 'GL', 'NP', 'CF', 'OX']),
  ...R('North', 2, ['LE', 'PE', 'NR', 'CV', 'NN', 'MK', 'SG', 'LU', 'AL', 'HP', 'CB', 'IP', 'CO', 'CM', 'SS']),
  ...R('North', 3, ['NE', 'CA', 'DH', 'SR', 'DL', 'TS', 'LA', 'YO', 'HU', 'BD', 'HG', 'LS', 'WF', 'HX', 'HD', 'OL', 'BL', 'BB', 'PR', 'FY', 'WN', 'L', 'M', 'WA', 'SK', 'S', 'DN', 'LN', 'CH', 'CW', 'NG', 'DE']),
  ...R('South', 1, ['EX', 'PL', 'TQ', 'TR']),
  ...R('South', 2, ['DT', 'SP', 'BH', 'SO']),
  ...R('South', 3, ['RG', 'GU', 'SL']),
  ...R('South', 4, ['ME', 'CT', 'TN', 'RH', 'BN', 'PO']),
  ...R('South', 5, ['BS', 'BA', 'TA', 'SN']),
};

export const ORBITAL_CODES = Object.keys(POSTCODE_REGIONS).filter(
  (c) => POSTCODE_REGIONS[c].region === 'Orbital'
);

export const areaColorFor = (code, { orbitalAsGrey = false } = {}) => {
  const info = POSTCODE_REGIONS[code];
  if (!info) return OUTSIDE_GREY;
  if (orbitalAsGrey && info.region === 'Orbital') return ORBITAL_GREY;
  return AREA_COLORS[info.area];
};

// ── Component ────────────────────────────────────────────────────────────────
// Reusable dynamic map. highlight: Set of postcode area codes (or null).
// Orbital (London) is hidden by default; when the highlighted codes are all
// orbital the map zooms into the orbital section (Clear returns to full view).
export function UKPostcodeMap({ highlight = null, onAreaClick }) {
  const [hovered, setHovered] = useState(null);

  const { mainFeatures, mainPath, insetFeatures, insetPath, orbitalCentroid } = useMemo(() => {
    const fc = feature(topo, topo.objects['uk-postcode-area']);
    // Source TopoJSON duplicates a couple of ids (GU, ZE) where one copy is a
    // tiny degenerate sliver — keep the largest geometry per id.
    const byId = new Map();
    fc.features.forEach((f) => {
      const prev = byId.get(f.id);
      if (!prev || geoArea(f) > geoArea(prev)) byId.set(f.id, f);
    });
    const feats = [...byId.values()];
    // Fit the view to the coverage regions (England & Wales). Scotland/NI stay
    // in the data (rendered, just cropped at the top) until we serve them.
    const coverageFeats = feats.filter((f) => POSTCODE_REGIONS[f.id]);
    const W = 640; const H = 660;
    const proj = geoMercator().fitExtent(
      [[12, 12], [W - 12, H - 12]],
      { type: 'FeatureCollection', features: coverageFeats }
    );
    const path = geoPath(proj);

    const orbital = feats.filter((f) => ORBITAL_CODES.includes(f.id));
    const iW = 300; const iH = 220;
    const iProj = geoMercator().fitSize([iW, iH], { type: 'FeatureCollection', features: orbital });
    const iPath = geoPath(iProj);

    const oc = path.centroid({ type: 'FeatureCollection', features: orbital });
    return { mainFeatures: feats, mainPath: path, insetFeatures: orbital, insetPath: iPath, orbitalCentroid: oc };
  }, []);

  const isHighlighting = highlight && highlight.size > 0;
  const hlOrbital = isHighlighting ? [...highlight].filter((c) => ORBITAL_CODES.includes(c)) : [];
  const hlMainland = isHighlighting
    ? [...highlight].filter((c) => !ORBITAL_CODES.includes(c) && POSTCODE_REGIONS[c])
    : [];
  const zoomOrbital = hlOrbital.length > 0 && hlMainland.length === 0;
  const showInsetOverlay = hlOrbital.length > 0 && hlMainland.length > 0;

  // Soft-zoom the main map to the highlighted areas so they sit in view.
  const zoomTransform = useMemo(() => {
    if (!isHighlighting || zoomOrbital || hlMainland.length === 0) return null;
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    mainFeatures.forEach((f) => {
      if (!highlight.has(f.id) || ORBITAL_CODES.includes(f.id)) return;
      const b = mainPath.bounds(f);
      if (!isFinite(b[0][0])) return;
      x0 = Math.min(x0, b[0][0]); y0 = Math.min(y0, b[0][1]);
      x1 = Math.max(x1, b[1][0]); y1 = Math.max(y1, b[1][1]);
    });
    if (!isFinite(x0)) return null;
    // Keep the orbital blob in frame when the brand also serves London.
    if (hlOrbital.length > 0 && orbitalCentroid && isFinite(orbitalCentroid[0])) {
      x0 = Math.min(x0, orbitalCentroid[0] - 30); y0 = Math.min(y0, orbitalCentroid[1] - 30);
      x1 = Math.max(x1, orbitalCentroid[0] + 30); y1 = Math.max(y1, orbitalCentroid[1] + 30);
    }
    const pad = 42;
    x0 -= pad; y0 -= pad; x1 += pad; y1 += pad;
    const k = Math.min(2.6, Math.max(1, Math.min(640 / (x1 - x0), 660 / (y1 - y0))));
    if (k <= 1.06) return null;
    const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
    return { k, tx: 320 - k * cx, ty: 306 - k * cy };
  }, [highlight, isHighlighting, zoomOrbital, hlMainland.length, hlOrbital.length, mainFeatures, mainPath, orbitalCentroid]);

  const zoomK = zoomTransform ? zoomTransform.k : 1;

  const areaClass = (code) => {
    if (!isHighlighting) return 'pm-area';
    return highlight.has(code) ? 'pm-area is-flash' : 'pm-area is-dim';
  };

  const renderArea = (f, path, { orbitalAsGrey }) => (
    <path
      key={f.id}
      d={path(f)}
      className={areaClass(f.id)}
      fill={areaColorFor(f.id, { orbitalAsGrey })}
      vectorEffect="non-scaling-stroke"
      onClick={() => onAreaClick && onAreaClick(f.id)}
      onMouseEnter={() => setHovered(f.id)}
      onMouseLeave={() => setHovered(null)}
      data-testid={`map-area-${f.id}`}
    >
      <title>{f.id}{POSTCODE_REGIONS[f.id] ? ` — ${POSTCODE_REGIONS[f.id].region} · Area ${POSTCODE_REGIONS[f.id].area}` : ' — outside coverage regions'}</title>
    </path>
  );

  const renderLabel = (f, path, minSize = 340, scaleK = 1) => {
    const b = path.bounds(f);
    const size = (b[1][0] - b[0][0]) * (b[1][1] - b[0][1]) * scaleK * scaleK;
    if (size < minSize) return null;
    const [x, y] = path.centroid(f);
    if (!isFinite(x) || !isFinite(y)) return null;
    const dimmed = isHighlighting && !highlight.has(f.id);
    return (
      <text
        key={`t-${f.id}`}
        x={x}
        y={y}
        className={`pm-label ${dimmed ? 'is-dim' : ''}`}
        style={scaleK !== 1 ? { fontSize: `${9 / scaleK}px` } : undefined}
      >
        {f.id}
      </text>
    );
  };

  const hoverChip = hovered && (
    <div className="pm-hover-chip" data-testid="map-hover-chip">
      <strong>{hovered}</strong>
      {POSTCODE_REGIONS[hovered]
        ? ` · ${POSTCODE_REGIONS[hovered].region} — Area ${POSTCODE_REGIONS[hovered].area}`
        : ' · outside coverage regions'}
    </div>
  );

  // ── Zoomed orbital view (selection is entirely within Orbital) ──
  if (zoomOrbital) {
    return (
      <div className="pm-map-wrap pm-zoomed" data-testid="uk-postcode-map">
        <span className="pm-zoom-title" data-testid="map-zoom-title">Orbital (London)</span>
        <svg viewBox="0 0 300 220" className="pm-svg-zoom" role="img" aria-label="Orbital coverage map">
          {insetFeatures.map((f) => renderArea(f, insetPath, { orbitalAsGrey: false }))}
          {insetFeatures.map((f) => renderLabel(f, insetPath, 40))}
        </svg>
        <span className="pm-zoom-note">Press Clear to return to the full map</span>
        {hoverChip}
      </div>
    );
  }

  return (
    <div className="pm-map-wrap" data-testid="uk-postcode-map">
      <svg viewBox="0 0 640 660" className="pm-svg-main" role="img" aria-label="UK coverage map">
        <g
          className="pm-zoom-layer"
          data-testid="map-zoom-layer"
          data-zoomed={zoomTransform ? 'true' : 'false'}
          style={{
            transform: zoomTransform
              ? `translate(${zoomTransform.tx}px, ${zoomTransform.ty}px) scale(${zoomTransform.k})`
              : 'translate(0px, 0px) scale(1)',
          }}
        >
          {mainFeatures.map((f) => renderArea(f, mainPath, { orbitalAsGrey: true }))}
          {mainFeatures
            .filter((f) => !ORBITAL_CODES.includes(f.id))
            .map((f) => renderLabel(f, mainPath, 340, zoomK))}
          {orbitalCentroid && isFinite(orbitalCentroid[0]) && (
            <text
              x={orbitalCentroid[0]}
              y={orbitalCentroid[1]}
              className="pm-label pm-label-orbital"
              style={zoomK !== 1 ? { fontSize: `${8 / zoomK}px` } : undefined}
            >
              ORBITAL
            </text>
          )}
        </g>
      </svg>

      {showInsetOverlay && (
        <div className="pm-inset" data-testid="map-orbital-inset">
          <span className="pm-inset-title">Orbital (London)</span>
          <svg viewBox="0 0 300 220" className="pm-svg-inset">
            {insetFeatures.map((f) => renderArea(f, insetPath, { orbitalAsGrey: false }))}
            {insetFeatures.map((f) => renderLabel(f, insetPath, 90))}
          </svg>
        </div>
      )}

      {hoverChip}
    </div>
  );
}
